<template>
  <q-card class="card">
    <q-card-section>
      <div class="row items-center q-mb-md">
        <div :class="`row justify-center items-center ${$q.screen.width < 450 ? 'full-width q-mb-md' : ''}`">
          <AssetPicture
            :class="$q.screen.width < 450 ? 'full-width' : 'q-mr-md'"
            :disable="saveInProgress"
            :imageUrl="imageUrl"
            :size="75"
            :type="assetType"
            @remove="
              imageUrl = '';
              onInput();
            "
            @update="
              imageUrl = $event;
              onInput();
            "
          />
        </div>
        <q-input
          v-model="nickname"
          autofocus
          class="col-grow"
          :disable="saveInProgress"
          filled
          hide-bottom-space
          label="Name"
          :rules="[(value) => Boolean(value) || 'Please enter a name.']"
          @input="onInput"
        />
      </div>
      <div data-t="assetTypeField">
        <q-select
          v-if="typeOptions.length > 0"
          v-model="assetTypeKey"
          v-capacitor-native-select
          class="q-mb-sm"
          :disable="saveInProgress"
          display-value-sanitize
          emit-value
          filled
          hide-bottom-space
          label="Type"
          map-options
          :options="typeOptions"
          options-sanitize
          :rules="[(value) => Boolean(value) || 'Please choose a type.']"
          @input="onInput"
        />
      </div>

      <q-banner v-if="typeOptions.length === 0" class="q-mb-sm bg-info text-white"
        >Please create a new type for this asset.</q-banner
      >
      <q-btn
        :class="{ 'q-pr-sm': true, error: assetTypeError }"
        color="primary"
        data-t="assetNewAssetType"
        dense
        flat
        icon="add_circle"
        label="New Asset Type"
        @click="showAddAssetTypeDialog = true"
      ></q-btn>
      <div v-if="assetTypeError" class="q-field__bottom q-ml-sm text-negative">{{ assetTypeError.message }}</div>
      <div>
        <ZubieSelect
          class="q-my-sm"
          data-t="assetTagsField"
          :disable="saveInProgress || availableTags.length === 0"
          display-value-sanitize
          emit-value
          filled
          hide-bottom-space
          label="Tags"
          map-options
          multiple
          option-label="value"
          option-value="key"
          :options="availableTags"
          options-sanitize
          popup-label="Selected Tags"
          use-chips
          :value.sync="tagKeys"
          @input="onInput"
        >
          <template v-slot:selected-item="scope">
            <TagChip
              dense
              :removable="true"
              :tabindex="scope.tabindex"
              :tag="scope.opt"
              @remove="tagKeys.splice(scope.index, 1)"
            />
          </template>
        </ZubieSelect>
        <q-tooltip v-if="availableTags.length === 0">There are no tags to choose from.</q-tooltip>
      </div>
      <div v-if="hasPermission('groupEdit')">
        <TreeViewMultiSelect
          class="q-mb-sm"
          data-t="assetGroupField"
          :dataTree="groups"
          :disable="saveInProgress || groups.length === 0"
          filled
          label="Group"
          nodeLabelKey="name"
          nodeValueKey="key"
          :singleSelection="true"
          :value="groupKeys"
          @change="
            groupKeys = $event;
            onInput();
          "
        />
        <q-tooltip v-if="groups.length === 0">There are no groups to choose from.</q-tooltip>
      </div>
    </q-card-section>
    <AddAssetTypeDialog
      v-if="showAddAssetTypeDialog"
      :show="showAddAssetTypeDialog"
      @hide="showAddAssetTypeDialog = false"
      @input="selectAssetType($event)"
    />
  </q-card>
</template>

<script>
import _isEmpty from 'lodash/isEmpty';
import { mapActions, mapGetters, mapState } from 'vuex';
import ZubieSelect from 'components/ZubieSelect';
import AddAssetTypeDialog from 'components/assets/AddAssetTypeDialog';
import AssetPicture from 'components/assets/AssetPicture';
import TagChip from 'components/tags/TagChip';
import TreeViewMultiSelect from 'components/TreeViewMultiSelect';
import { ASSET_TYPES } from 'src/services/constants';

export default {
  name: 'AssetForm',
  props: {
    asset: Object,
    assetTypeError: Error,
    saveInProgress: Boolean,
  },
  components: {
    AddAssetTypeDialog,
    AssetPicture,
    TagChip,
    TreeViewMultiSelect,
    ZubieSelect,
  },
  computed: {
    ...mapState('assets', ['assetTypes']),
    ...mapState('groups', ['groups']),
    ...mapGetters('session', ['hasPermission']),
    ...mapGetters('tags', {
      availableTags: 'genericTags',
    }),
    assetType() {
      return ASSET_TYPES.ASSET;
    },
    typeOptions() {
      return Object.values(this.assetTypes).map(({ key, name }) => ({
        label: name,
        value: key,
      }));
    },
  },
  data() {
    return {
      groupKeys: [],
      imageUrl: null,
      nickname: null,
      tagKeys: [],
      assetTypeKey: null,
      showAddAssetTypeDialog: false,
    };
  },
  methods: {
    ...mapActions('groups', ['getGroups']),
    ...mapActions('tags', ['getTags']),
    onInput() {
      const { assetTypeKey, groupKeys, imageUrl, nickname, tagKeys } = this;
      this.$emit('input', {
        assetTypeKey,
        groupKeys,
        imageUrl,
        nickname,
        tagKeys,
      });
    },
    selectAssetType(assetType) {
      this.assetTypeKey = assetType.key;
      this.onInput();
    },
  },
  async mounted() {
    if (!this.availableTags.length) {
      await this.getTags();
    }

    if (this.groups.length === 0) {
      await this.getGroups();
    }

    if (!_isEmpty(this.asset)) {
      this.groupKeys = this.asset.groupKeys;
      this.imageUrl = this.asset.imageUrl;
      this.nickname = this.asset.nickname;
      this.tagKeys = this.asset.tagKeys;
      this.assetTypeKey = this.asset.assetTypeKey;
      this.onInput();
    }
  },
};
</script>

<style lang="stylus" scoped>
.error {
  border: 2px solid $negative;
}
</style>
